import axios, { AxiosError } from "axios";
import axiosInstance from "../auth/axiosInstance";
import type {
  AskReimagineChatRequest,
  AskReimagineHealthResponse,
  AskReimagineStartRequest,
  AskReimagineTurnResponse,
} from "../types/axlPilotContext";

type ApiEnvelope<T> = {
  data?: T;
  succeeded?: boolean;
  message?: string;
  code?: number;
};

function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiEnvelope<unknown> & {
      detail?: string;
    };

    if (typeof data?.message === "string" && data.message) {
      return data.message;
    }

    if (typeof data?.detail === "string" && data.detail) {
      return data.detail;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Request failed";
}

function assertApiSuccess<T>(envelope: ApiEnvelope<T> | undefined): T {
  const failed =
    envelope?.succeeded === false ||
    (typeof envelope?.code === "number" && envelope.code >= 400);

  if (failed) {
    throw new Error(envelope?.message || "Request failed");
  }

  if (envelope?.data == null) {
    throw new Error(envelope?.message || "Empty response from server");
  }

  return envelope.data;
}

export async function getAskReimagineHealth(): Promise<AskReimagineHealthResponse> {
  try {
    const { data: envelope } = await axiosInstance.get<
      ApiEnvelope<AskReimagineHealthResponse>
    >("/AskReimagine/health", { timeout: 30000 });
    return assertApiSuccess(envelope);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
}

export async function startAskReimagineAssistant(
  body: AskReimagineStartRequest
): Promise<AskReimagineTurnResponse> {
  try {
    const { data: envelope } = await axiosInstance.post<
      ApiEnvelope<AskReimagineTurnResponse>
    >("/AskReimagine/assistant/start", body, { timeout: 60000 });
    return assertApiSuccess(envelope);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
}

export async function sendAskReimagineChat(
  body: AskReimagineChatRequest
): Promise<AskReimagineTurnResponse> {
  try {
    const { data: envelope } = await axiosInstance.post<
      ApiEnvelope<AskReimagineTurnResponse>
    >("/AskReimagine/assistant/chat", body, { timeout: 120000 });
    return assertApiSuccess(envelope);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
}
